import { useMemo, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Divider,
    Chip,
    Snackbar,
    Alert,
} from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import ShareIcon from "@mui/icons-material/Share";
import CloseIcon from "@mui/icons-material/Close";
import moment from "moment";

import { ITrip } from "@interfaces";
import {
    groupItemSpansByDay,
    dateForDayIndex,
    selectedTravellerCount,
    computeTripTotal,
    buildItineraryText,
    buildItineraryPrintHtml,
} from "src/Utilities/tripPlannerHelpers";

interface TripItineraryProps {
    open: boolean;
    trip: ITrip;
    onClose: () => void;
}

/**
 * Read-only, day-by-day view of the trip. Derives everything from the trip
 * state (no fetching) so it's a pure projection of what the user has built.
 * Supports printing (opens an isolated print window) and sharing (native share
 * sheet with a clipboard fallback).
 */
export const TripItinerary = ({ open, trip, onClose }: TripItineraryProps) => {
    const days = useMemo(() => groupItemSpansByDay(trip), [trip]);
    const total = useMemo(() => computeTripTotal(trip), [trip]);
    const [toast, setToast] = useState<{ msg: string; severity: "success" | "error" } | null>(
        null,
    );

    const handlePrint = () => {
        // Print from an isolated window so MUI's portal/global styles don't
        // interfere and the user only prints the itinerary itself. Both the
        // title and body are HTML-escaped in buildItineraryPrintHtml so a
        // hostile trip/item name can't inject markup into the print window.
        const win = window.open("", "_blank", "width=720,height=900");
        if (!win) {
            setToast({ msg: "Please allow pop-ups to print your itinerary.", severity: "error" });
            return;
        }
        win.document.write(buildItineraryPrintHtml(trip));
        win.document.close();
        win.focus();
        win.print();
    };

    const handleShare = async () => {
        const text = buildItineraryText(trip);
        const title = trip.name?.trim() || "My Trip";
        try {
            const nav = navigator as Navigator & {
                share?: (data: { title?: string; text?: string }) => Promise<void>;
            };
            if (typeof nav.share === "function") {
                await nav.share({ title, text });
                return;
            }
            if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(text);
                setToast({ msg: "Itinerary copied to clipboard", severity: "success" });
                return;
            }
            setToast({ msg: "Sharing isn't supported on this device.", severity: "error" });
        } catch {
            // User cancelling the native share sheet rejects the promise; treat
            // that as a no-op rather than surfacing a scary error.
        }
    };

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
                <DialogTitle>
                    <Box>
                        <Typography variant="h6" component="div">
                            {trip.name?.trim() || "My Trip"}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {trip.durationDays} day{trip.durationDays === 1 ? "" : "s"} from{" "}
                            {moment(trip.startDate, "YYYY-MM-DD").format("ddd, D MMM YYYY")}
                        </Typography>
                    </Box>
                </DialogTitle>
                <DialogContent dividers>
                    {days.map((items, dayIndex) => {
                        const dateLabel = moment(
                            dateForDayIndex(trip.startDate, dayIndex),
                            "YYYY-MM-DD",
                        ).format("ddd, D MMM YYYY");
                        return (
                            <Box key={dayIndex} sx={{ mb: 2.5 }}>
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "baseline",
                                        gap: 1,
                                        mb: 1,
                                    }}
                                >
                                    <Typography variant="subtitle1" fontWeight={700}>
                                        Day {dayIndex + 1}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {dateLabel}
                                    </Typography>
                                </Box>
                                {items.length === 0 ? (
                                    <Typography
                                        variant="body2"
                                        color="text.disabled"
                                        sx={{ pl: 1 }}
                                    >
                                        No items planned.
                                    </Typography>
                                ) : (
                                    items.map(({ item, isStart }) => {
                                        const filled = selectedTravellerCount(item);
                                        if (!isStart) {
                                            // Continuation day of a multi-day stay/tour: show
                                            // a muted line so the day reads as occupied without
                                            // repeating the fare/price (avoids double-counting).
                                            return (
                                                <Box
                                                    key={item.id}
                                                    sx={{
                                                        pl: 1,
                                                        py: 0.5,
                                                        borderLeft: "3px dashed",
                                                        borderColor: "divider",
                                                        mb: 0.75,
                                                    }}
                                                >
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                        fontStyle="italic"
                                                    >
                                                        {item.name} (continued)
                                                    </Typography>
                                                </Box>
                                            );
                                        }
                                        return (
                                            <Box
                                                key={item.id}
                                                sx={{
                                                    pl: 1,
                                                    py: 0.75,
                                                    borderLeft: "3px solid",
                                                    borderColor: "primary.light",
                                                    mb: 0.75,
                                                }}
                                            >
                                                <Typography variant="body1" fontWeight={600}>
                                                    {item.name}
                                                </Typography>
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        flexWrap: "wrap",
                                                        gap: 0.75,
                                                        mt: 0.5,
                                                        alignItems: "center",
                                                    }}
                                                >
                                                    {item.fareName ? (
                                                        <Typography
                                                            variant="body2"
                                                            color="text.secondary"
                                                        >
                                                            {item.fareName}
                                                        </Typography>
                                                    ) : null}
                                                    <Chip
                                                        size="small"
                                                        label={`Qty ${item.quantity}`}
                                                        variant="outlined"
                                                    />
                                                    <Chip
                                                        size="small"
                                                        label={`$${(
                                                            item.rrp * item.quantity
                                                        ).toFixed(0)}`}
                                                        variant="outlined"
                                                    />
                                                    <Chip
                                                        size="small"
                                                        color={filled > 0 ? "primary" : "default"}
                                                        label={`${filled}/${item.quantity} travellers`}
                                                        variant={filled > 0 ? "filled" : "outlined"}
                                                    />
                                                </Box>
                                            </Box>
                                        );
                                    })
                                )}
                                <Divider sx={{ mt: 1.5 }} />
                            </Box>
                        );
                    })}
                    <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
                        <Typography variant="subtitle1" fontWeight={700}>
                            Total: ${total.toFixed(0)}
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button startIcon={<ShareIcon />} onClick={handleShare}>
                        Share
                    </Button>
                    <Button startIcon={<PrintIcon />} onClick={handlePrint}>
                        Print
                    </Button>
                    <Button startIcon={<CloseIcon />} onClick={onClose} variant="contained">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
            <Snackbar
                open={!!toast}
                autoHideDuration={3000}
                onClose={() => setToast(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                {toast ? (
                    <Alert
                        onClose={() => setToast(null)}
                        severity={toast.severity}
                        variant="filled"
                        sx={{ width: "100%" }}
                    >
                        {toast.msg}
                    </Alert>
                ) : undefined}
            </Snackbar>
        </>
    );
};

export default TripItinerary;
